/**
 * Agreement → Portfolios (Banks arc step 4, migration 0115; Pedro L7 option B + P1–P6,
 * 2026-10-21).
 *
 * Read mode (P4): "the read-only view of just that Agreement's Portfolios, shown as they
 * appear in Portfolios\View Portfolio, with all the view customization that exists there" —
 * View portfolio itself, limited to this agreement's portfolios.
 * Edit mode: link a stored portfolio (one not linked elsewhere — P2, at most one agreement at
 * a time) or unlink one. Pattern A (§3g): staged here (orange), committed by the page's Save,
 * an RM's change filed for Compliance (blue, with its change request), as agreement parties.
 * Admin can also assign centrally in Portfolios → Assign to… (P1 / P6).
 * Full screen (PF-POP, Pedro 2026-10-21: "pop out to a fully functional full width page which
 * then closes back down when we leave"): the same View portfolio grows to cover the window and
 * shrinks back; one instance, so the chosen portfolios, date, view and columns carry over.
 * Opening in full screen (auto, then "only full screen") was tried and dropped once pages
 * became wider (Pedro: "bring back Portfolios opening as per usual under the tab but with the
 * full page button, and upon close ... coming back to the initial "in-tab" Portfolio view").
 */
import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Box, Button, Group, Select, Stack, Table, Text, Tooltip } from '@mantine/core';
import { IconArrowBackUp, IconArrowsMaximize, IconArrowsMinimize, IconLink, IconLinkOff } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { agreementPortfoliosApi } from '../api/agreementPortfolios';
import type { UUID } from '../api/types';
import { usePendingCreates } from '../hooks/usePendingCreates';
import { ViewTab, type ViewRequest } from '../pages/PortfolioViewTab';
import { PendingCRBadge } from './PendingCRBadge';

export type PendingPortfolioOp =
  | { kind: 'unlink'; link_id: UUID; label: string }
  | { kind: 'link'; tempKey: string; bank_id: UUID; portfolio_number: string; label: string };

export const portfolioOpKey = (op: PendingPortfolioOp): string =>
  op.kind === 'link' ? op.tempKey : op.link_id;

/** MR-C: the currencies offered for an agreement's Preferred Ref Ccy (its current one is kept). */
const PREFERRED_REF_CCYS = ['EUR', 'USD', 'GBP', 'CHF', 'JPY', 'CAD', 'AUD', 'SEK', 'NOK', 'DKK', 'HKD', 'SGD', 'BRL'];

/** MR-C (0126, Pedro 2026-10-26): "Agreement Portfolio carry a Preferred Ref Ccy, left justified, at
 *  the same height as the Full Screen button. That is a Ref Ccy preset that is memorized and is read
 *  by the existing Ref Ccy field." An agreement field: changed in Edit mode, saved with Save. */
function PreferredRefCcy({ value, input }: {
  value: string;
  input?: { value?: unknown; onChange?: (v: unknown) => void };
}) {
  const current = input ? String(input.value ?? value) : value;
  const data = [...new Set([...PREFERRED_REF_CCYS, current])];
  return (
    <Tooltip withArrow disabled={!!input}
      label="Change it in Edit mode. It sets the Ref Ccy this tab opens in, and the currency of the Monthly Report's portfolio pages.">
      <Group gap={6} wrap="nowrap">
        <Text size="xs" c="dimmed">Preferred Ref Ccy</Text>
        <Select size="xs" w={90} data={data} value={current} allowDeselect={false} searchable
          disabled={!input} onChange={(v) => { if (v && input?.onChange) input.onChange(v); }} />
      </Group>
    </Tooltip>
  );
}

export function AgreementPortfoliosSection({
  agreementId, editMode, tradable, openRequest, pendingOps, failedKeys, onStageLink, onStageUnlink, onUnstage,
  preferredRef = 'EUR', preferredRefInput,
}: {
  agreementId: UUID;
  editMode: boolean;
  /** N2 (Pedro 2026-10-25): trade mode here, for holders of `trades.draft` (one portfolio shown). */
  tradable?: boolean;
  /** N4: a Trades row asked to open a draft (its portfolio, in trade mode). */
  openRequest?: ViewRequest;
  pendingOps: readonly PendingPortfolioOp[];
  failedKeys: ReadonlySet<string>;
  onStageLink: (bank_id: UUID, portfolio_number: string, label: string) => void;
  onStageUnlink: (link_id: UUID, label: string) => void;
  onUnstage: (key: string) => void;
  /** MR-C: the saved Preferred Ref Ccy (the view's preset), and its form input in Edit mode. */
  preferredRef?: string;
  preferredRefInput?: { value?: unknown; onChange?: (v: unknown) => void };
}) {
  const linksQ = useQuery({ queryKey: ['agreement-portfolios', 'list', agreementId],
    queryFn: () => agreementPortfoliosApi.list(agreementId) });
  const links = linksQ.data ?? [];
  const filed = usePendingCreates('agreement_portfolios', 'agreement_id', agreementId);
  const scope = useMemo(() => links.map((l) => ({ ref: l.ref, label: l.label })), [links]);
  const [full, setFull] = useState(false);
  const fullScreen = full && !editMode;
  useEffect(() => {
    if (!fullScreen) return;
    // Esc closes full screen only when no picker / menu / popover inside is open (that Esc is
    // theirs). Listened in the capture phase, so it is checked before the dropdown closes.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Mantine keeps closed dropdowns mounted with display:none and sets no aria-expanded.
      const dropdownOpen = [...document.querySelectorAll<HTMLElement>(
        '.mantine-Popover-dropdown, .mantine-Combobox-dropdown, .mantine-Menu-dropdown',
      )].some((d) => getComputedStyle(d).display !== 'none');
      if (dropdownOpen) return;
      setFull(false);
    };
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey, true);
    window.dispatchEvent(new Event('resize'));   // the grid re-measures its height
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = overflow;
      window.dispatchEvent(new Event('resize'));
    };
  }, [fullScreen]);

  if (linksQ.error) return <Alert color="red">{(linksQ.error as Error).message}</Alert>;

  const pendingAny = filed.length > 0 || links.some((l) => l.pending_change_requests.length > 0);
  // TRADE-CONS 1a: the agreement's consolidations (set in Portfolios → Assign to…).
  const consolidations = [...links.reduce((m, l) => {
    if (l.consolidation) m.set(l.consolidation.name, [...(m.get(l.consolidation.name) ?? []), l.label]);
    return m;
  }, new Map<string, string[]>())].sort(([a], [b]) => a.localeCompare(b));
  if (!editMode) {
    return (
      <Stack gap="xs">
        {consolidations.length > 0 && (
          <Group gap={6}>
            <Tooltip withArrow multiline w={340} label="Portfolios matched to be traded as one. Changed in Portfolios → Assign to….">
              <Text size="xs" c="dimmed" style={{ cursor: 'help' }}>Consolidations:</Text>
            </Tooltip>
            {consolidations.map(([name, labels]) => (
              <Tooltip key={name} withArrow multiline w={340} label={labels.join(' · ')}>
                <Badge variant="outline" color="gray">{name} · {labels.length} portfolios</Badge>
              </Tooltip>
            ))}
          </Group>
        )}
        {pendingAny && (
          <Group gap={6}>
            <Text size="xs" c="dimmed">Filed for approval:</Text>
            {filed.map((cr) => (
              <Badge key={cr.id} component={Link} to={`/change-requests/${cr.id}`} color="blue" variant="light"
                style={{ cursor: 'pointer' }}>
                Pending · Link {String((cr.payload as Record<string, unknown>).portfolio_number ?? '')}
              </Badge>
            ))}
            {links.filter((l) => l.pending_change_requests.length > 0).map((l) => (
              <Group key={l.id} gap={4}><Text size="xs">{l.label}</Text><PendingCRBadge crs={l.pending_change_requests} /></Group>
            ))}
          </Group>
        )}
        {links.length === 0 && !linksQ.isLoading ? (
          <Text size="sm" c="dimmed">
            No portfolio is linked to this agreement. Link one in Edit mode (or, for Admin, in Portfolios → Assign
            to…).
          </Text>
        ) : links.length > 0 && (
          <Box style={fullScreen ? {
            // Above the app shell (Mantine 100), below popovers / menus / modals (200–300),
            // so the grid's own pickers and menus still open on top.
            position: 'fixed', inset: 0, zIndex: 150, overflow: 'auto',
            background: 'var(--mantine-color-body)', padding: '12px 16px',
          } : undefined}>
            <Group justify="space-between" mb="xs" wrap="nowrap">
              <Group gap="md" wrap="nowrap">
                {fullScreen && (
                  <Text fw={600}>Portfolios{links[0]?.agreement_label ? ` · ${links[0].agreement_label}` : ''}</Text>
                )}
                <PreferredRefCcy value={preferredRef} />
              </Group>
              {fullScreen ? (
                <Tooltip label="Back to the agreement (Esc)">
                  <Button size="xs" variant="light" leftSection={<IconArrowsMinimize size={14} />}
                    onClick={() => setFull(false)}>Close full screen</Button>
                </Tooltip>
              ) : (
                <Tooltip label="Show this view across the whole window; close it to come back here">
                  <Button size="xs" variant="light" leftSection={<IconArrowsMaximize size={14} />}
                    onClick={() => setFull(true)}>Full screen</Button>
                </Tooltip>
              )}
            </Group>
            <ViewTab scope={scope} tradable={tradable} request={openRequest?.n ? openRequest : undefined}
              preferredRef={preferredRef} />
          </Box>
        )}
      </Stack>
    );
  }
  return (
    <Stack gap="xs">
      <PreferredRefCcy value={preferredRef} input={preferredRefInput} />
      <EditLinks links={links} filed={filed.map((cr) => ({ id: cr.id, number: String((cr.payload as Record<string, unknown>).portfolio_number ?? '') }))}
        pendingOps={pendingOps} failedKeys={failedKeys}
        onStageLink={onStageLink} onStageUnlink={onStageUnlink} onUnstage={onUnstage} />
    </Stack>
  );
}

/** Why a link cannot be unlinked now, or null (TRADE-CONS 1a, Pedro S1-Q4: a consolidation
 *  member stays linked until it is taken out of the consolidation). */
function unlinkBlock(l: { pending_change_requests: unknown[]; consolidation: { name: string } | null }): string | null {
  if (l.pending_change_requests.length > 0) return 'A change to this link is filed for approval: wait for it';
  if (l.consolidation) return `In consolidation ${l.consolidation.name}: take it out of the consolidation first (Portfolios → Assign to… → Attach to Consolidations: set its Consolidation to None)`;
  return null;
}

function EditLinks({ links, filed, pendingOps, failedKeys, onStageLink, onStageUnlink, onUnstage }: {
  links: Awaited<ReturnType<typeof agreementPortfoliosApi.list>>;
  filed: { id: UUID; number: string }[];
  pendingOps: readonly PendingPortfolioOp[];
  failedKeys: ReadonlySet<string>;
  onStageLink: (bank_id: UUID, portfolio_number: string, label: string) => void;
  onStageUnlink: (link_id: UUID, label: string) => void;
  onUnstage: (key: string) => void;
}) {
  const unlinkedQ = useQuery({ queryKey: ['agreement-portfolios', 'overview', 'unlinked'],
    queryFn: () => agreementPortfoliosApi.overview({ unlinked: true }) });
  const [pick, setPick] = useState<string | null>(null);
  const stagedLinks = pendingOps.filter((o): o is Extract<PendingPortfolioOp, { kind: 'link' }> => o.kind === 'link');
  const unlinking = new Map(pendingOps.filter((o) => o.kind === 'unlink').map((o) => [(o as { link_id: UUID }).link_id, o]));
  const taken = new Set(stagedLinks.map((o) => `${o.bank_id}:${o.portfolio_number}`));
  // Not linked anywhere and not filed (a portfolio an RM filed elsewhere would 409 on approval).
  const options = (unlinkedQ.data?.items ?? [])
    .filter((r) => r.bank_id && !taken.has(r.ref) && r.pending_links.length === 0)
    .map((r) => ({ value: r.ref, label: `${r.label} · positions of ${r.latest_date}` }));
  const byRef = Object.fromEntries((unlinkedQ.data?.items ?? []).map((r) => [r.ref, r]));

  return (
    <Stack gap="sm">
      <Table withTableBorder fz="sm" verticalSpacing={4}>
        <Table.Thead>
          <Table.Tr><Table.Th>Portfolio</Table.Th><Table.Th w={140}>Latest positions</Table.Th><Table.Th w={240} /></Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {links.map((l) => {
            const staged = unlinking.get(l.id);
            const failed = failedKeys.has(l.id);
            return (
              <Table.Tr key={l.id} bg={failed ? 'var(--mantine-color-red-0)' : staged ? 'var(--mantine-color-orange-0)' : undefined}>
                <Table.Td>
                  <Group gap={6}>
                    {l.label}
                    {l.consolidation && <Badge size="sm" variant="outline" color="gray">{l.consolidation.name}</Badge>}
                  </Group>
                </Table.Td>
                <Table.Td>{l.latest_date ?? '-'}</Table.Td>
                <Table.Td>
                  <Group gap={6} justify="flex-end">
                    <PendingCRBadge crs={l.pending_change_requests} />
                    {failed && <Badge color="red" variant="light">Failed</Badge>}
                    {staged ? (
                      <>
                        <Badge color="orange" variant="light">Pending Save · Unlink</Badge>
                        <Tooltip withArrow label="Keep it linked (undo)">
                          <Button size="compact-xs" variant="subtle" onClick={() => onUnstage(l.id)}><IconArrowBackUp size={14} /></Button>
                        </Tooltip>
                      </>
                    ) : (
                      <Tooltip withArrow multiline w={320} disabled={!unlinkBlock(l)} label={unlinkBlock(l)}>
                        <span>
                          <Button size="compact-xs" variant="light" color="red" leftSection={<IconLinkOff size={14} />}
                            disabled={!!unlinkBlock(l)} onClick={() => onStageUnlink(l.id, l.label)}>
                            Unlink
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            );
          })}
          {stagedLinks.map((o) => (
            <Table.Tr key={o.tempKey} bg={failedKeys.has(o.tempKey) ? 'var(--mantine-color-red-0)' : 'var(--mantine-color-orange-0)'}>
              <Table.Td>{o.label}</Table.Td>
              <Table.Td>-</Table.Td>
              <Table.Td>
                <Group gap={6} justify="flex-end">
                  {failedKeys.has(o.tempKey) && <Badge color="red" variant="light">Failed</Badge>}
                  <Badge color="orange" variant="light">Pending Save · Link</Badge>
                  <Tooltip withArrow label="Remove (undo)">
                    <Button size="compact-xs" variant="subtle" onClick={() => onUnstage(o.tempKey)}><IconArrowBackUp size={14} /></Button>
                  </Tooltip>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
          {filed.map((f) => (
            <Table.Tr key={f.id} bg="var(--mantine-color-blue-0)">
              <Table.Td>{f.number}</Table.Td>
              <Table.Td>-</Table.Td>
              <Table.Td>
                <Group justify="flex-end">
                  <Badge component={Link} to={`/change-requests/${f.id}`} color="blue" variant="light" style={{ cursor: 'pointer' }}>
                    Pending Approval · Link
                  </Badge>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
          {links.length + stagedLinks.length + filed.length === 0 && (
            <Table.Tr><Table.Td colSpan={3}><Text size="sm" c="dimmed">No portfolio linked yet.</Text></Table.Td></Table.Tr>
          )}
        </Table.Tbody>
      </Table>
      <Group align="flex-end">
        <Select label="Link a portfolio" size="xs" w={380} searchable clearable value={pick} onChange={setPick}
          placeholder={unlinkedQ.isLoading ? 'Loading…' : options.length ? 'Choose a portfolio not linked yet' : 'Every portfolio is linked'}
          data={options} nothingFoundMessage="No portfolio not linked yet matches" />
        <Button size="xs" leftSection={<IconLink size={14} />} disabled={!pick}
          onClick={() => {
            const r = pick ? byRef[pick] : null;
            if (r?.bank_id) onStageLink(r.bank_id, r.portfolio_number, r.label);
            setPick(null);
          }}>
          Add (on Save)
        </Button>
      </Group>
      <Text size="xs" c="dimmed">
        Only portfolios with stored positions and not linked to another agreement are offered: a portfolio belongs to one
        agreement at a time.
      </Text>
    </Stack>
  );
}
